import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { OrderCartEntry } from '../pages/v2-order-pay/models/order-cart-entry.model';
import {
  ItemModifierGroupUI,
  ItemModifierUI
} from '../pages/v2-order-pay/models/item-modifier-ui.model';
import { filter, map } from 'rxjs/operators';
import { IOrderMenuItem } from '../pages/v2-order-pay/models/order-menu.models';
import { v4 as uuid } from 'uuid';
import { ISelectedItemDetails } from './item-details.store';
import { LocationStore } from './location.store';
import { IOrderMenuItemRecommendation } from '../pages/v2-order-pay/models/order-menu-item-recommendation.model';
import { ArrayUtil } from 'utility/array.util';

@Injectable({
  providedIn: 'root'
})
export class OrderMenuCartStore {
  constructor(private locationStore: LocationStore) {}

  private readonly _cartEntries$ = new BehaviorSubject<OrderCartEntry[]>([]);
  readonly cartEntries$ = this._cartEntries$.asObservable();

  get cartEntries(): OrderCartEntry[] {
    return this._cartEntries$.getValue();
  }

  private readonly _currentEditCartEntryId$ = new BehaviorSubject<
    string | null
  >(null);

  get currentEditCartEntryId(): string | null {
    return this._currentEditCartEntryId$.getValue();
  }

  set currentEditCartEntryId(v: string | null) {
    this._currentEditCartEntryId$.next(v);
  }

  private readonly _newlyAddedCartEntry$ =
    new BehaviorSubject<OrderCartEntry | null>(null);
  readonly newlyAddedCartEntry$ = this._newlyAddedCartEntry$.pipe(
    filter((e) => !!e)
  ) as Observable<OrderCartEntry>;

  // will only add selected modifiers to cart
  saveItemToCart(
    item: IOrderMenuItem,
    modifierGroups: ItemModifierGroupUI[],
    details: ISelectedItemDetails
  ) {
    const filteredModifierGroups =
      this.filterModifiersForAddingToCart(modifierGroups);

    if (this.currentEditCartEntryId) {
      this._cartEntries$.next(
        this._cartEntries$.getValue().map((entry) => {
          if (entry.id === this.currentEditCartEntryId) {
            entry.modifierGroups = filteredModifierGroups;
            entry.quantity = details.quantity;
            entry.comment = details.comment;
          }
          return entry;
        })
      );
    } else {
      const cartEntry: OrderCartEntry = {
        id: uuid(),
        item,
        quantity: details.quantity,
        comment: details.comment,
        modifierGroups: filteredModifierGroups
      };

      this._cartEntries$.next([...this._cartEntries$.getValue(), cartEntry]);
      this._newlyAddedCartEntry$.next(cartEntry);
    }
  }

  private filterModifiersForAddingToCart(
    modifierGroups: ItemModifierGroupUI[]
  ): ItemModifierGroupUI[] {
    return modifierGroups.map((mg) => ({
      ...mg,
      modifiers: mg.modifiers
        .filter((m) => m.isSelected)
        .map((m) => ({
          ...m,
          modifierGroups: this.filterModifiersForAddingToCart(m.modifierGroups)
        }))
    }));
  }

  removeEntryFromCart(entry: OrderCartEntry) {
    this._cartEntries$.next(
      this._cartEntries$.getValue().filter((e) => e.id !== entry.id)
    );
  }

  clearCart() {
    this._cartEntries$.next([]);
  }

  readonly cartTotal$: Observable<number> = this.cartEntries$.pipe(
    map((entries) =>
      entries.reduce((a, entry) => a + this.getCartEntryTotal(entry), 0)
    )
  );

  getCartEntryTotal(entry: OrderCartEntry): number {
    return (
      (entry.item.price + this.totalModifierGroups(entry.modifierGroups)) *
      entry.quantity
    );
  }

  totalModifierGroups(modifierGroups: ItemModifierGroupUI[]): number {
    let price = 0;
    modifierGroups.forEach((group) => {
      group.modifiers
        .filter((m) => m.isSelected)
        .forEach((mod) => {
          price += this.totalModifierPrice(mod);
        });
    });
    return price;
  }

  totalModifierPrice(modifier: ItemModifierUI): number {
    let price = modifier.price;
    price += this.totalModifierGroups(modifier.modifierGroups);
    return price * (modifier.quantity || 1);
  }

  private readonly _userHasAcceptedAlcoholPolicy$ =
    new BehaviorSubject<boolean>(false);

  set userHasAcceptedAlcoholPolicy(v: boolean) {
    this._userHasAcceptedAlcoholPolicy$.next(v);
  }

  private readonly cartHasAlcohol$: Observable<boolean> =
    this.cartEntries$.pipe(
      map((cartEntries) =>
        cartEntries.some((entry) => entry.item.attributes.alcohol)
      )
    );

  readonly alcoholConfirmationRequired$: Observable<boolean> = combineLatest([
    this.cartHasAlcohol$,
    this._userHasAcceptedAlcoholPolicy$,
    this.locationStore.currentLocation$
  ]).pipe(
    map(([cartHasAlcohol, userHasAcceptedAlcoholPolicy, location]) => {
      if (!cartHasAlcohol) {
        return false;
      } else if (userHasAcceptedAlcoholPolicy) {
        return false;
      } else {
        return !!location.alcoholPolicy;
      }
    })
  );

  private readonly cartEntryItems$: Observable<IOrderMenuItem[]> =
    this.cartEntries$.pipe(
      map((cartEntries) => cartEntries.map((e) => e.item))
    );

  private readonly maxNumberOfItemRecommendations = 5;

  readonly recommendedItems$: Observable<IOrderMenuItemRecommendation[]> =
    this.cartEntryItems$.pipe(
      map((cartEntryItems) =>
        this.curateRecommendedItems(
          cartEntryItems,
          this.maxNumberOfItemRecommendations
        )
      )
    );

  private curateRecommendedItems(
    cartEntryItems: IOrderMenuItem[],
    maxNumberOfItemRecommendations = this.maxNumberOfItemRecommendations
  ): IOrderMenuItemRecommendation[] {
    const recommendedItems: IOrderMenuItemRecommendation[] = [];
    const totalRecommendations = cartEntryItems.reduce(
      (a, item) =>
        a + item.recommendedItems.filter((item) => item.inStock).length,
      0
    );
    let currentRecommendedArrayDepth = 0;

    while (recommendedItems.length < totalRecommendations) {
      cartEntryItems.forEach((item) => {
        const recommendedItemForDepth = item.recommendedItems.filter(
          (item) => item.inStock
        )[currentRecommendedArrayDepth];
        if (recommendedItemForDepth) {
          recommendedItems.push({
            ...recommendedItemForDepth,
            recommendationOriginator: item
          });
        }
      });
      currentRecommendedArrayDepth++;
    }

    const itemIdBlacklist = cartEntryItems.map((item) => item.id);
    return (
      Array.from(
        new Set(
          recommendedItems
            .map((item) => item.id)
            .filter((itemId) => !itemIdBlacklist.includes(itemId))
        )
      )
        .map((itemId) => recommendedItems.find((item) => item.id === itemId))
        .filter(
          (itemRecommendation) => !!itemRecommendation
        ) as IOrderMenuItemRecommendation[]
    ).slice(0, maxNumberOfItemRecommendations);
  }

  private readonly _popularMenuItemIds$ = new BehaviorSubject<string[]>([]);

  setPopularMenuItemIds(v: string[]) {
    this._popularMenuItemIds$.next(v);
  }

  private readonly _menuPopularItems$ = new BehaviorSubject<IOrderMenuItem[]>(
    []
  );

  setMenuPopularItems(v: IOrderMenuItem[]) {
    this._menuPopularItems$.next(v);
  }

  private readonly maxNumberOfPopularItems = 5;

  readonly popularItems$: Observable<IOrderMenuItem[]> = combineLatest([
    this.cartEntryItems$,
    this.recommendedItems$,
    this._menuPopularItems$
  ]).pipe(
    map(([cartEntryItems, recommendedItems, menuPopularItems]) =>
      this.curatePopularItems(
        menuPopularItems,
        cartEntryItems,
        recommendedItems
      )
    )
  );

  private curatePopularItems(
    menuPopularItems: IOrderMenuItem[],
    cartEntryItems: IOrderMenuItem[],
    recommendedItems: IOrderMenuItemRecommendation[],
    maxNumberOfPopularItems = this.maxNumberOfPopularItems
  ): IOrderMenuItem[] {
    const popularItems = menuPopularItems.filter(
      (popularItem) =>
        !cartEntryItems.find((cartItem) => cartItem.id === popularItem.id) &&
        !recommendedItems.find((recItem) => recItem.id === popularItem.id)
    );

    return ArrayUtil.shuffleArray(popularItems)
      .sort((a, b) => +b.inStock - +a.inStock)
      .slice(0, maxNumberOfPopularItems);
  }

  readonly showPopularMenuItems$: Observable<boolean> = this.popularItems$.pipe(
    map((popularItems) => popularItems.length > 0)
  );

  getModifierTotal(modifier: ItemModifierUI): number {
    return modifier.price * modifier.quantity;
  }
}
